import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { produce } from "immer";
import moment from "moment";
import { isEmpty } from "underscore";
import { AppDispatch } from "..";
import { ProductContractTypeFilter } from "../../api/common";
import { GetVehicleAssignmentsMyFleetQuery, GetVehicleAssignmentsQuery } from "../../api/parkingProducts.api";
import { getAppInsights } from "../../appInsights";
import { DateFilter } from "../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FmpFilters } from "../../constants/filtering.constants";
import { AssignedParkingRight, DelegeesWithPagination, DelegeeWithProductsModel, InviteDelegeeResponse, UpdateDelegeeResponse, VehicleAssignmentsMyFleetOverviewWithPagination } from "../../models/delegations/DelegatedParkingProductsModel";
import { ErrorDetailsModel, tryAxios, ValidationResult } from "../../models/ErrorDetailsModel";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../../models/ForFleetManager";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { ParkingProductModel } from "../../models/ParkingProductModel";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { VehicleAssignmentProduct } from "../../models/vehicle-assignments/VehicleAssignmentProduct";
import parkingProductsService from "../../services/parkingProducts.service";
import { parkingProductsTypes } from "../action-types/parkingProducts.types";
import { RootReducer } from "../reducers";
import { loaderActions } from "./loader.actions";
import { checkForCrmIdOrLinkedAccessDevice } from "./parkingProducts.toast";
import { toastActions } from "./toast.actions";
import { checkForSeasonTicketCrmIdNotValidError } from "../../utils";

type ThunkApiConfig = {
    rejectValue: ErrorDetailsModel;
    state: RootReducer;
};

export const getLocatedParkingProductsThunk = createAsyncThunk<
    LocatedParkingProduct[],
    { seasonTicketOwnerCrmId: string; language: string },
    ThunkApiConfig
>(parkingProductsTypes.GET_LOCATED_PARKING_PRODUCTS,
    ({ seasonTicketOwnerCrmId, language }, { dispatch, getState, rejectWithValue }) => {
        const { locatedParkingProducts } = getState().parkingProducts;

        if (isEmpty(locatedParkingProducts.data)) {
            dispatch(loaderActions.loadTheLoader());
        }

        return parkingProductsService
            .getLocatedParkingProducts(seasonTicketOwnerCrmId, language)
            .then((response: AxiosResponse<LocatedParkingProduct[]>) => {
                return response.data;
            })
            .catch((error: AxiosError) => {
                return rejectWithValue(error.response?.data as ErrorDetailsModel);
            })
            .finally(() => dispatch(loaderActions.killTheLoader()));
    });

export const getParkingProducts = createAsyncThunk<
    ParkingProductModel[],
    { seasonTicketOwnerCrmId: string; location: string; unlimitedOnly?: boolean },
    ThunkApiConfig
>(
    parkingProductsTypes.GET_PARKING_PRODUCTS,
    async ({ seasonTicketOwnerCrmId, location, unlimitedOnly }, { rejectWithValue }) => {
            try {
                const response = await parkingProductsService.getParkingProducts(seasonTicketOwnerCrmId, location);
                return unlimitedOnly ? response.data.filter(p => p.hasUnlimitedEntryRights) : response.data;
            } catch (error) {
                if (error instanceof AxiosError) {
                    return rejectWithValue(error.response?.data as ErrorDetailsModel);
                }

                throw error;
            }
    }
);

export const delegateParkingRightsThunk = createAsyncThunk<
    ValidationResult<InviteDelegeeResponse>,
    ParkingRightsRequestModel,
    ThunkApiConfig>(
        parkingProductsTypes.DELEGATE_PARKING_RIGHTS,
        async (model, { dispatch }) => {
            const delegateResult = await tryAxios(parkingProductsService.delegateParkingRights(model), _ => {
                dispatch(toastActions.error({
                    content: {
                        key: "parkingRights.inviteDelegee.error",
                    },
                }));
            });

            if (!delegateResult.ok) {
                checkForCrmIdOrLinkedAccessDevice(dispatch, delegateResult.error);
                if (checkForSeasonTicketCrmIdNotValidError(delegateResult.error)) {
                    dispatch(toastActions.error(({
                        content: {
                            key: "errors.invalidSeasonTicketCrmId",
                        },
                    })));
                } else {
                    dispatch(toastActions.error({
                    content: {
                        key: "parkingRights.inviteDelegee.error",
                    },
                    }));
                }
            }

            return delegateResult;
        }
    );

export type GetAssignedVehicleResponse = {
    pageNumber: number;
    totalPages?: number;
    totalRecords?: number;
    parkingRights?: AssignedParkingRight[];
};

export type GetVehicleAssignmentsQueryWithExcludes = GetVehicleAssignmentsQuery & {
    vehiclesToExclude?: VehicleAssignmentProduct[],
    excludeByVehicleAssignmentId?: boolean
    countersOnly?: boolean
};

export const getVehiclesThunk = createAsyncThunk<
    GetAssignedVehicleResponse,
    GetVehicleAssignmentsQueryWithExcludes,
    ThunkApiConfig
>(
    parkingProductsTypes.GET_ASSIGNED_VEHICLES,
    async (query, { signal, rejectWithValue }) => {
        try {
            const { vehiclesToExclude, excludeByVehicleAssignmentId } = query;
            // We overfetch the page because possible we have to remove the revoked/deleted rows
            if (vehiclesToExclude) {
                query = {...query, pageSize: query.pageSize + vehiclesToExclude.length};
            }

            const response = await parkingProductsService.getVehicles(query, { signal });

            // vehicles that are revokes/deleted can still be in projection, thus we exclude them directly after revoke/delete is called
            if (vehiclesToExclude) {
                const rowsToExclude = response.data.assignedParkingRights.filter(p =>
                    vehiclesToExclude.some(v => excludeByVehicleAssignmentId ?
                        v.vehicleAssignmentId === p.vehicleAssignmentId :
                        v.vehicleAssignmentId === p.vehicleAssignmentId && v.pmcId === p.parkingRight.pmc));
                response.data.totalRecords -= rowsToExclude.length;
                response.data.assignedParkingRights = response.data.assignedParkingRights.filter(x => !rowsToExclude.includes(x));
            }

            return {
                pageNumber: query.page,
                totalPages: response.data.pages,
                totalRecords: response.data.totalRecords,
                parkingRights: response.data.assignedParkingRights
            };
        } catch (error) {
            if (error instanceof AxiosError) {
                return rejectWithValue(error.response?.data);
            }
            throw error;
        }
    });

export function refreshVehiclesMyProducts(customize?: (q :GetVehicleAssignmentsQueryWithExcludes) => GetVehicleAssignmentsQueryWithExcludes) {
    return (dispatch: AppDispatch, getState: () => RootReducer) => {
        const { pages } = getState().parkingProducts.vehiclesMyProducts;
        for (const page of Object.values(pages)) {
            const query = customize ? customize(page.query) : page.query;
            dispatch(getVehiclesThunk(query));
        }
    }
}


export type UpdateDelegeeThunkPayload = {
    response: UpdateDelegeeResponse;
    original?: DelegeeWithProductsModel
};

export const updateDelegationThunk = createAsyncThunk<
    ValidationResult<UpdateDelegeeThunkPayload>,
    ParkingRightsRequestModel,
    ThunkApiConfig>(
        parkingProductsTypes.UPDATE_DELEGATION,
        async (model, { dispatch, getState }) => {
            const showErrorToast = () => dispatch(toastActions.error({
                content: {
                    key: "parkingRights.updateDelegation.error",
                },
            }));
            try {
                const original = getState().parkingProducts.delegeeDetails.data || undefined;

                const updateResult = await tryAxios(parkingProductsService.updateDelegation(model), _ => {
                    showErrorToast();
                });
                if (!updateResult.ok) {
                    if(checkForSeasonTicketCrmIdNotValidError(updateResult.error)) {
                        dispatch(toastActions.error({
                            content: {
                                key: "errors.invalidSeasonTicketCrmId"
                            }
                        }));
                    } else {
                        showErrorToast();
                    }
                    return updateResult;
                }
                dispatch(toastActions.success({
                    content: {
                        key: "parkingRights.updateDelegation.success",
                    },
                }));
                return {
                    ...updateResult,
                    value: {
                        response: updateResult.value,
                        original,
                    }
                };
            }
            catch (error) {
                if (error instanceof Error) {
                    getAppInsights()?.trackException({
                        exception: error,
                        properties: {
                            action: "UpdateDelegation",
                            registrationId: model.registrationId,
                            pmcIds: model.products.map(p => p.pmcId),
                        }
                    });
                }
                throw error;
            }

        }
    );

export const getDelegeesThunk = createAsyncThunk<
    DelegeesWithPagination,
    { request: WithFiltersAndPaging<ForFleetManager>, productContractTypeFilter: ProductContractTypeFilter },
    ThunkApiConfig>(
        parkingProductsTypes.GET_DELEGEES,
        async ({ request, productContractTypeFilter }, { rejectWithValue }) => {
            try {
                const processedRequest = produce(request, draft => {
                    const fmpFilter = draft.filters?.find(f => f.key === FmpFilters.invitationDate);
                    const val = fmpFilter?.value as DateFilter;
                    if (val?.startDate) {
                        // to utc using moment
                        val.startDate = moment.utc(val.startDate).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate();
                    }
                    if (val?.endDate) {
                        // this one is sent to CC and Delegee Inquiry
                        // CC truncates time portion in ef core, delegee inquiry does not
                        val.endDate = moment.utc(val.endDate).set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toDate();
                    }
                });

                const response = await parkingProductsService.getDelegees(processedRequest, productContractTypeFilter);
                return response.data;
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return rejectWithValue(error.response?.data);
                }
                throw error;
            }
        }
    );

export const getDelegeeDetailsThunk = createAsyncThunk<
    DelegeeWithProductsModel,
    { registrationId: string; language: string; seasonTicketOwnerCrmId: string },
    ThunkApiConfig>(
        parkingProductsTypes.GET_DELEGEE_DETAILS,
        async ({ registrationId, language, seasonTicketOwnerCrmId }, { rejectWithValue }) => {
            try {
                const response = await parkingProductsService.getDelegeeDetails(registrationId, language, seasonTicketOwnerCrmId);
                return response.data;
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return rejectWithValue(error.response?.data);
                }
                throw error;
            }
        }
    );

export const getGetVehicleAssignmentsMyFleetThunk = createAsyncThunk<
    VehicleAssignmentsMyFleetOverviewWithPagination,
    { query: GetVehicleAssignmentsMyFleetQuery, excludedIds: string[] },
    ThunkApiConfig>(
        parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET_OVERVIEW,
            async ({ query: baseQuery, excludedIds }, { rejectWithValue }) => {
            const originalPageSize = baseQuery.pageSize;
            // overfetch to fill the page in case deleted items are still in the projection
            const query = { ...baseQuery, pageSize: baseQuery.pageSize + excludedIds.length };
            try {
                const response = await parkingProductsService.getVehicleAssignmentsMyFleetOverview(query);
                const toRemove = response.data.vehicleAssignments.filter(x => excludedIds.includes(x.vehicleAssignmentId));
                const vehicle = response.data.vehicleAssignments.filter(x => !toRemove.includes(x)).slice(0, originalPageSize);

                response.data.totalRecords = response.data.totalRecords - toRemove.length;
                const uniqueVehiclesRemoved = new Set(toRemove.map(x => x.vehicleAssignmentId)).size;
                response.data.totalUniqueVehicles = response.data.totalUniqueVehicles - uniqueVehiclesRemoved;
                response.data.vehicleAssignments = vehicle;

                return response.data;
            }
            catch (error) {
                if (error instanceof AxiosError) {
                    return rejectWithValue(error.response?.data);
                }
                throw error;
            }
        }
    );
